import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductGrid from './components/ProductGrid';
import ProductFilters from './components/ProductFilters';
import FeaturedProductsSection from './components/FeaturedProductsSection';
import QuoteModal from '../../components/feature/QuoteModal';
import { featuredProducts } from '../../mocks/products';

export default function Products() {
  const { t } = useTranslation('common');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');

  const handleRequestQuote = (name: string) => {
    setSelectedProduct(name);
    setIsQuoteModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Banner */}
      <section
        className="relative bg-cover bg-center h-[280px] md:h-[360px] flex items-center"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/a5b00f276b94a8c6e19269aefafefb92.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 to-black/30"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 w-full">
          <div className="max-w-xl">
            <p className="text-teal-400 text-xs md:text-sm font-semibold uppercase tracking-widest mb-3">
              {t('products.gbGroupCatalogue')}
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {t('products.pageTitle')}
            </h1>
            <p className="text-gray-300 text-sm md:text-base max-w-md">
              {t('products.pageSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* GB Group Own Brand Showcase */}
      <section className="bg-gradient-to-r from-gray-950 via-gray-900 to-gray-950 py-0 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center gap-0 md:gap-12">
            {/* Image */}
            <div className="relative flex-shrink-0 flex items-end justify-center w-full md:w-[340px] pt-6 md:pt-0">
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-16 bg-teal-500/10 rounded-full blur-2xl"></div>
              <img
                src="https://public.readdy.ai/ai/img_res/9fadc2d2-6e4f-4066-882a-4cd85e5cca5f.png"
                alt="GB Group Truck Spare Parts - Disk Brake Pads packaging"
                className="relative z-10 w-[160px] sm:w-[200px] md:w-[360px] object-contain drop-shadow-2xl"
                style={{ marginBottom: '-2px' }}
              />
            </div>

            {/* Text Content */}
            <div className="flex-1 py-6 md:py-14 text-center md:text-left">
              <span className="inline-block bg-teal-600/20 text-teal-400 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-3">
                {t('products.ownBrandBadge')}
              </span>
              <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-white mb-3 leading-tight">
                {t('products.showcaseTitle1')}<br />
                <span className="text-teal-400">{t('products.showcaseTitle2')}</span>
              </h2>
              <p className="text-gray-400 text-sm md:text-base max-w-lg mx-auto md:mx-0 mb-4 leading-relaxed">
                {t('products.showcaseDesc')}
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-5">
                {([
                  t('products.badgeOEM'),
                  t('products.badgeEuroTrucks'),
                  t('products.badgeLongLasting'),
                  t('products.badgeInStock'),
                ] as string[]).map((badge) => (
                  <span key={badge} className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-gray-300 text-xs font-medium px-3 py-1.5 rounded-full">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-check-line text-teal-400 text-xs"></i>
                    </div>
                    {badge}
                  </span>
                ))}
              </div>
              <button
                onClick={() => handleRequestQuote('GB Group Brake Pads for European Trucks')}
                className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold px-6 py-2.5 rounded-xl hover:bg-teal-500 active:scale-95 transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-file-text-line"></i>
                </div>
                {t('products.requestQuote')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <FeaturedProductsSection
        products={featuredProducts}
        onRequestQuote={handleRequestQuote}
      />

      {/* Full Catalog */}
      <section className="py-8 md:py-14">
        <div className="container mx-auto px-4 md:px-6">
          {/* Section Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-8 bg-teal-600 rounded-full flex-shrink-0"></div>
            <div>
              <p className="text-xs font-semibold text-teal-600 uppercase tracking-widest mb-0.5">
                {t('products.fullCatalogue')}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t('products.browseAllParts')}</h2>
            </div>
          </div>

          {/* Filters: search + category tabs */}
          <div className="mb-8">
            <ProductFilters
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>

          {/* Products Grid */}
          <ProductGrid
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
          />
        </div>
      </section>

      {/* CTA Banner */}
      <section
        className="relative py-10 md:py-16 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://readdy.ai/api/search-image?query=Close%20up%20view%20of%20high%20quality%20heavy%20duty%20truck%20engine%20parts%20mechanical%20components%20industrial%20background%20warm%20metallic%20tones%20professional%20commercial%20vehicle%20workshop&width=1920&height=500&seq=products-cta-bg&orientation=landscape)',
        }}
      >
        <div className="absolute inset-0 bg-teal-900/80"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            {t('products.cantFind')}
          </h2>
          <p className="text-teal-200 text-sm md:text-base max-w-lg mx-auto mb-8">
            {t('products.cantFindDesc')}
          </p>
          <button
            onClick={() => setIsQuoteModalOpen(true)}
            className="inline-flex items-center gap-2 bg-white text-teal-700 font-semibold px-8 py-3.5 rounded-xl hover:bg-teal-50 active:scale-95 transition-all whitespace-nowrap cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-file-text-line"></i>
            </div>
            {t('products.requestCustomQuote')}
          </button>
        </div>
      </section>

      <Footer />

      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        productName={selectedProduct}
      />
    </div>
  );
}
